/* iter-p2-o — Public boxed-set landing at /bookstore/bundles/:slug.
 *
 * Hero image = Nano-Banana `og_image_url` (falls back to `cover_url` /
 * the first book's cover). Below the hero: title, tagline, save-%
 * badge, per-book list, "Buy the boxed set" CTA that hits the
 * existing /api/bookstore/checkout/bundle route.
 */
import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, ShoppingBag, BookOpen, Tag, ArrowRight } from 'lucide-react';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
const usd = (cents) => `$${((Number(cents) || 0) / 100).toFixed(2)}`;

export default function BundleLandingPage() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/bookstore/bundles/by-slug/${slug}`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Bundle not found.'));
  }, [slug]);

  const buy = async () => {
    if (busy || !data?.bundle?.id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/bookstore/checkout/bundle`, {
        bundle_id:  data.bundle.id,
        origin_url: window.location.origin,
      }, { withCredentials: true });
      if (r.data?.url) window.location.href = r.data.url;
      else toast.error('Checkout URL missing from response.');
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not start checkout.';
      if (e?.response?.status === 401) {
        toast.error('Please sign in to buy this boxed set.');
        nav('/bookstore/login');
      } else {
        toast.error(msg);
      }
    } finally { setBusy(false); }
  };

  if (err) {
    return (
      <div className="mx-auto max-w-md p-8 text-center" data-testid="bl-error">
        <div className="text-[16px] font-semibold">Bundle unavailable</div>
        <p className="mt-1 text-[13px] text-muted-foreground">{err}</p>
        <Link to="/bookstore" className="mt-3 inline-block text-[12px] text-primary underline">
          Browse the store
        </Link>
      </div>
    );
  }
  if (!data) {
    return <div className="flex min-h-screen items-center justify-center"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  const { bundle, books } = data;
  const heroSrc = bundle.og_image_url || bundle.cover_url
                    || books[0]?.cover_url || books[0]?.cover_front_url || '';
  const discountPct = bundle.discount_pct || 0;
  const savedCents = Math.max(0, (bundle.regular_price_cents || 0) - (bundle.bundle_price_cents || 0));

  return (
    <div className="min-h-screen w-full bg-background text-foreground" data-testid="bl-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border">
        {heroSrc && (
          <img src={heroSrc} alt=""
               className="absolute inset-0 h-full w-full object-cover opacity-40"
               data-testid="bl-hero-bg" />
        )}
        <div className="relative mx-auto max-w-4xl px-6 py-16">
          <Link to="/bookstore"
                className="mb-4 inline-block text-[11px] text-primary hover:underline">
            ← Back to the store
          </Link>
          {discountPct > 0 && (
            <div className="mb-3 inline-flex items-center gap-1 rounded-sm bg-primary/15 px-2 py-0.5 text-[10.5px] font-semibold uppercase tracking-wider text-primary"
                 data-testid="bl-save-badge">
              <Tag className="h-3 w-3" /> Save {discountPct}%
            </div>
          )}
          <h1 className="text-[36px] leading-tight sm:text-[48px]"
              style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 600 }}
              data-testid="bl-title">
            {bundle.title}
          </h1>
          {bundle.tagline && (
            <p className="mt-2 max-w-2xl text-[14.5px] italic text-foreground/85"
               data-testid="bl-tagline">
              &ldquo;{bundle.tagline}&rdquo;
            </p>
          )}
          <div className="mt-6 flex flex-wrap items-baseline gap-3">
            <div className="font-mono-stat text-[32px] font-semibold text-primary"
                 data-testid="bl-price">
              {usd(bundle.bundle_price_cents)}
            </div>
            {bundle.regular_price_cents > bundle.bundle_price_cents && (
              <div className="text-[14px] text-muted-foreground line-through"
                   data-testid="bl-regular">
                {usd(bundle.regular_price_cents)}
              </div>
            )}
            {savedCents > 0 && (
              <div className="text-[12px] text-emerald-700 dark:text-emerald-400">
                You save {usd(savedCents)}
              </div>
            )}
          </div>
          <Button size="lg" onClick={buy} disabled={busy}
                  className="mt-6 h-12 gap-2 px-6 text-[14px]"
                  data-testid="bl-buy-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShoppingBag className="h-4 w-4" />}
            Buy the boxed set
            <ArrowRight className="h-4 w-4" />
          </Button>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Instant EPUB, PDF, and Kindle delivery for all {books.length} books.
          </p>
        </div>
      </section>

      {/* Book list */}
      <section className="mx-auto max-w-4xl px-6 py-12" data-testid="bl-book-list">
        <div className="mb-1 text-[10.5px] uppercase tracking-wider text-primary">
          Included in this boxed set
        </div>
        <h2 className="text-[22px] font-semibold"
            style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          {books.length} novels, one price
        </h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {books.map((b, i) => (
            <Card key={b.id} className="flex gap-3 p-4"
                  data-testid={`bl-book-${b.id}`}>
              {(b.cover_url || b.cover_front_url) ? (
                <img src={b.cover_url || b.cover_front_url} alt="Cover"
                     className="h-28 w-20 flex-none rounded-sm border border-border object-cover" />
              ) : (
                <div className="flex h-28 w-20 flex-none items-center justify-center rounded-sm border border-border bg-panel-header/40">
                  <BookOpen className="h-6 w-6 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">
                  Book {i + 1}
                </div>
                <div className="mt-0.5 truncate text-[15px] font-semibold">
                  {b.title}
                </div>
                <div className="text-[11.5px] text-muted-foreground">
                  {b.author_name || b.author}
                </div>
                {b.tagline && (
                  <p className="mt-1 line-clamp-2 text-[11.5px] italic text-muted-foreground">
                    {b.tagline}
                  </p>
                )}
                <div className="mt-1.5 text-[10.5px] text-muted-foreground">
                  Normally {usd(b.price_cents)}
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}
